import { useRef, useState, useMemo, useCallback } from 'react';
import AGGridMain from '@/components/tables/AGGrid/AGGridMain';
import PlatformFilterStack from '@/components/tables/AGGrid/PlatformFilterStack';
import { useSelector } from 'react-redux';
import { HStack, useDisclosure, ModalCloseButton } from '@chakra-ui/react';
import { renderTelephoneNumber } from '../../../../utils/utils';
import { axios } from 'services/axios';
import ChakraButton from 'components/v4/Button';
import { useSingleToast } from 'hooks/useSingleToast';
import {
    useDashboardServices,
    useDeactivate,
    useQuery,
} from 'hooks/useCustomQuery';
import PlatformCellRenderer from '../../../../components/tables/AGGrid/CustomCells/PlatformCellRenderer';
import StatusCellRenderer from '../../numbers/edit/cellRenderer/StatusCellRenderer';
import { FaUserMinus, FaUnlink } from 'react-icons/fa';
import Platforms from '../../../../utils/platforms';
import getCompanyPlatforms from 'API/Company/getCompanyPlatforms';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Alert,
    AlertIcon,
    AlertDescription,
} from 'components/v4';
import ActionMenu from 'components/presentational/ActionMenu';
/*
 *This component is used for de-allocating the numbers from a user
 *Once this has happened the entire number block can be deleted
 *The number block cannot be deleted until all numbers are deallocated
 */

const DeleteNumberBlocksTable = () => {
    const tableRef = useRef();

    // state access
    const { child } = useSelector((state) => {
        return { ...state.table };
    });

    const { requiredScope } = useSelector((state) => {
        return { ...state.login };
    });

    const { successToast, errorToast } = useSingleToast();

    // Modal states
    const {
        isOpen: isDeallocateUserOpen,
        onOpen: onDeallocateUserOpen,
        onClose: onDeallocateUserClose,
    } = useDisclosure();
    const {
        isOpen: isDeallocateTrunkOpen,
        onOpen: onDeallocateTrunkOpen,
        onClose: onDeallocateTrunkClose,
    } = useDisclosure();
    const {
        isOpen: isDeallocateAllUsersOpen,
        onOpen: onDeallocateAllUsersOpen,
        onClose: onDeallocateAllUsersClose,
    } = useDisclosure();
    const {
        isOpen: isDeallocateAllTrunksOpen,
        onOpen: onDeallocateAllTrunksOpen,
        onClose: onDeallocateAllTrunksClose,
    } = useDisclosure();
    const {
        isOpen: isDisassociateOpen,
        onOpen: onDisassociateOpen,
        onClose: onDisassociateClose,
    } = useDisclosure();
    const {
        isOpen: isDisassociateAllOpen,
        onOpen: onDisassociateAllOpen,
        onClose: onDisassociateAllClose,
    } = useDisclosure();
    const [selectedAction, setSelectedAction] = useState(null);
    const [isBusy, setIsBusy] = useState(false);
    const [modalError, setModalError] = useState(null);

    const { data: numberBlockDetail } = useQuery({
        queryKey: ['numberBlockDetail', child?.id],
        queryFn: async () => {
            const res = await axios.get(`/numberblock/${child.id}`);
            return res.data;
        },
        enabled: Boolean(child?.id),
    });

    const blockCompanyID = numberBlockDetail?.companyID;

    const { data: presentableNumbers = [] } = useQuery({
        queryKey: ['presentableNumbers', blockCompanyID],
        queryFn: async () => {
            const res = await axios.get(
                `/company/${blockCompanyID}/numbers/presentable`,
            );
            return res.data;
        },
        enabled: Boolean(blockCompanyID),
    });

    const { data: services } = useDashboardServices({
        currentCompany: blockCompanyID,
        options: { enabled: Boolean(blockCompanyID) },
    });
    const { mutateAsync: deactivateNumbers } = useDeactivate();

    const mapNumberRows = useCallback(
        (items = []) =>
            items.map((item) => ({
                id: item.numberID,
                telephoneNumber: item.telephoneNumber,
                wholesaler: child.wholesalerName || '',
                partner: child.partnerName || '',
                company: child.companyName || '',
                status: item.numberStatus,
                isEnabled: item.isEnabled,
                platformNumbers: item.platformNumbers || [],
            })),
        [child],
    );

    const { data: companyPlatforms = [] } = useQuery({
        queryKey: [
            'numberBlockCompanyPlatforms',
            blockCompanyID,
            requiredScope,
        ],
        queryFn: () => getCompanyPlatforms(requiredScope, blockCompanyID),
        enabled: Boolean(blockCompanyID),
    });

    const platformList = useMemo(() => {
        if (!companyPlatforms.length) return [];
        return companyPlatforms
            .filter((p, i, self) => i === self.findIndex((q) => q.id === p.id))
            .map((p) => ({ id: p.id, name: Platforms[p.id]?.name || p.name }));
    }, [companyPlatforms]);

    // Fetch data
    const [rowData, setRowData] = useState([]);
    const [selectedPlatformFilter, setSelectedPlatformFilter] =
        useState(undefined);

    const { isLoading: loading, refetch: refetchPlatformNumbers } = useQuery({
        queryKey: ['numberBlockPlatformNumbers', child?.id],
        queryFn: async () => {
            const res = await axios.get(
                `/NumberBlock/${child.id}/PlatformNumbers`,
            );
            return res.data;
        },
        enabled: Boolean(child?.id),
        onSuccess: (data) => setRowData(mapNumberRows(data)),
    });

    const hasOCMobileNumbers = rowData
        .flatMap((n) => n.platformNumbers)
        .some((n) => n.operatorConnectUsageID === 'OCMobile');

    // Handler functions for actions
    const handleDeallocateUser = useCallback(
        async (platformNumber) => {
            setModalError(null);
            setIsBusy(true);
            try {
                await axios.delete('/UserProfile', {
                    params: {
                        platformID: platformNumber.platformID,
                        NumberID: platformNumber.numberID,
                        platformuserid: platformNumber.userID,
                    },
                });
                successToast({ title: 'User deallocated successfully' });
                await refetchPlatformNumbers();
                return true;
            } catch (error) {
                const errorMsg =
                    error.response?.data?.message ||
                    error.message ||
                    'Failed to deallocate user';
                setModalError(errorMsg);
                return false;
            } finally {
                setIsBusy(false);
            }
        },
        [refetchPlatformNumbers, successToast],
    );

    const handleDeallocateTrunk = useCallback(
        async (platformNumber) => {
            setModalError(null);
            setIsBusy(true);
            try {
                await axios.put('/PlatformNumber', {
                    platformID: platformNumber.platformID,
                    numberID: platformNumber.numberID,
                    trunkID: '',
                    priority: platformNumber.priority || 0,
                    overstampTrunkID: platformNumber.overstampTrunkID || null,
                    overstampNumber: platformNumber.overstampNumber || null,
                    operatorConnectOperatorID:
                        platformNumber.operatorConnectOperatorID || null,
                    operatorConnectCallingProfileID:
                        platformNumber.operatorConnectCallingProfileID || null,
                    operatorConnectUsageID:
                        platformNumber.operatorConnectUsageID || null,
                    overrideDefaultRegistration:
                        platformNumber.overrideDefaultRegistration || false,
                    registrationID: platformNumber.registrationID || null,
                    addressID: platformNumber.addressID || null,
                    attribute1: platformNumber.attribute1 || null,
                    attribute2: platformNumber.attribute2 || null,
                    attribute3: platformNumber.attribute3 || null,
                });
                successToast({ title: 'Trunk deallocated successfully' });
                await refetchPlatformNumbers();
                return true;
            } catch (error) {
                const errorMsg =
                    error.response?.data?.message ||
                    error.message ||
                    'Failed to deallocate trunk';
                setModalError(errorMsg);
                return false;
            } finally {
                setIsBusy(false);
            }
        },
        [refetchPlatformNumbers, successToast],
    );

    const handleDisassociate = useCallback(
        async (platformNumber) => {
            try {
                setIsBusy(true);
                await deactivateNumbers({
                    serviceID: services?.find(
                        (s) => s.name === 'Teams Phone Mobile',
                    )?.id,
                    phoneNumbers: [platformNumber.telephoneNumber],
                });
                successToast({ title: 'Number disassociated successfully' });
                await refetchPlatformNumbers();
            } catch (error) {
                errorToast({
                    title: 'Failed to disassociate number',
                    description: error.message,
                });
            } finally {
                setIsBusy(false);
            }
        },
        [
            deactivateNumbers,
            errorToast,
            refetchPlatformNumbers,
            services,
            successToast,
        ],
    );

    // AGGridMain columns - Top level (master)
    const columns = useMemo(
        () => [
            {
                maxWidth: 70,
                width: 70,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellRenderer: 'agGroupCellRenderer',
                lockPosition: 'left',
            },
            {
                field: 'telephoneNumber',
                headerName: 'Number',
                valueGetter: (params) =>
                    renderTelephoneNumber(params.data, 'telephoneNumber'),
                width: 200,
                flex: 0.5,
            },
            {
                headerName: 'Wholesaler',
                field: 'wholesaler',
                hide: requiredScope < 80,
            },
            {
                headerName: 'Partner',
                field: 'partner',
            },
            {
                headerName: 'Company',
                field: 'company',
            },
        ],
        [requiredScope],
    );

    // Platform numbers columns (detail)
    const platformNumberColumns = useMemo(
        () => [
            {
                field: 'platformID',
                headerName: 'Platform',
                width: 120,
                flex: 0.6,
                cellRenderer: (params) => (
                    <PlatformCellRenderer value={params.value} />
                ),
                cellStyle: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
            },
            {
                field: 'platformStatus',
                headerName: 'Status',
                cellRenderer: (params) => {
                    const value =
                        params.data?.platformStatus === 0 && !params.data?.trunk
                            ? 'disabled'
                            : params.value;
                    return (
                        <StatusCellRenderer
                            value={value}
                            statusMessage={params.data?.statusMessage}
                            parent={false}
                        />
                    );
                },
                cellStyle: { display: 'flex', alignItems: 'center' },
            },
            {
                headerName: 'User',
                field: 'user',
            },
            {
                headerName: 'Trunk',
                field: 'trunk',
            },
            {
                headerName: 'Action',
                lockPosition: 'right',
                width: 120,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: (params) => {
                    const hasUser = !!params.data.user;
                    const hasTrunk = !!params.data.trunk;
                    const isTeamsPlatform =
                        params.data.platformID ===
                        'fac64979-385b-42ce-8b1c-d30e87822304';
                    const isTPM =
                        params.data.operatorConnectUsageID === 'OCMobile';
                    const showDisassociate =
                        hasOCMobileNumbers && isTeamsPlatform && isTPM;

                    const actionItems = [
                        {
                            label: 'Deallocate User',
                            icon: FaUserMinus,
                            onClick: () => {
                                setSelectedAction(params.data);
                                onDeallocateUserOpen();
                            },
                            dataAttributes: {
                                isDisabled: !hasUser,
                            },
                        },
                        {
                            label: 'Deallocate Trunk',
                            icon: FaUnlink,
                            onClick: () => {
                                setSelectedAction(params.data);
                                onDeallocateTrunkOpen();
                            },
                            dataAttributes: {
                                // Disable if trunk is not present OR disassociation is possible
                                isDisabled: !hasTrunk || showDisassociate,
                            },
                        },
                    ];

                    if (showDisassociate) {
                        actionItems.push({
                            label: 'Disassociate',
                            icon: FaUnlink,
                            onClick: () => {
                                setSelectedAction(params.data);
                                onDisassociateOpen();
                            },
                            dataAttributes: {
                                isDisabled: false,
                            },
                        });
                    }

                    return (
                        <ActionMenu
                            actionItems={actionItems}
                            menuLabel="Actions"
                        />
                    );
                },
            },
        ],
        [
            onDeallocateUserOpen,
            onDeallocateTrunkOpen,
            onDisassociateOpen,
            hasOCMobileNumbers,
        ],
    );

    // Fetch data
    // Detail cell renderer params for nested platform numbers
    const detailCellRendererParams = useMemo(
        () => ({
            detailGridOptions: {
                pagination: false,
                columnDefs: platformNumberColumns,
                headerHeight: 38,
                defaultColDef: {
                    flex: 1,
                    sortable: true,
                    filter: true,
                    resizable: true,
                    minWidth: 100,
                    suppressHeaderMenuButton: true,
                },
            },
            getDetailRowData: (params) => {
                const platformNumbers = params.data.platformNumbers || [];
                const filtered = selectedPlatformFilter
                    ? platformNumbers.filter(
                          (pn) => pn.platformID === selectedPlatformFilter,
                      )
                    : platformNumbers;
                params.successCallback(filtered);
            },
        }),
        [platformNumberColumns, selectedPlatformFilter],
    );

    // Compute display data and columns based on platform filter
    const displayConfig = useMemo(() => {
        if (selectedPlatformFilter) {
            // Flat view: show only platform numbers for selected platform
            const flatPlatformNumbers = rowData.flatMap((row) =>
                row.platformNumbers
                    .filter((pn) => pn.platformID === selectedPlatformFilter)
                    .map((pn) => ({
                        ...pn,
                        numberID: row.id,
                        telephoneNumber: row.telephoneNumber,
                        wholesaler: row.wholesaler,
                        partner: row.partner,
                        company: row.company,
                    })),
            );

            return {
                columns: platformNumberColumns,
                data: flatPlatformNumbers,
                masterDetail: false,
                isRowMaster: null,
                detailCellRendererParams: null,
            };
        } else {
            // Master-detail view: show all numbers with nested platform numbers
            return {
                columns: columns,
                data: rowData,
                masterDetail: true,
                isRowMaster: (dataItem) =>
                    dataItem.platformNumbers &&
                    dataItem.platformNumbers.length > 0,
                detailCellRendererParams: detailCellRendererParams,
            };
        }
    }, [
        rowData,
        selectedPlatformFilter,
        columns,
        platformNumberColumns,
        detailCellRendererParams,
    ]);

    // No need to filter top-level rows - filtering is done at nested level in detailCellRendererParams

    const handleDeallocateAllUsers = useCallback(async () => {
        try {
            setIsBusy(true);
            const allPlatformNumbers = rowData.flatMap(
                (n) => n.platformNumbers,
            );
            const numbersToProcess = selectedPlatformFilter
                ? allPlatformNumbers.filter(
                      (pn) => pn.platformID === selectedPlatformFilter,
                  )
                : allPlatformNumbers;
            const numbersWithUsers = numbersToProcess.filter(
                (pn) => pn.platformStatus === 0 && pn.userID,
            );
            for (const pn of numbersWithUsers) {
                await axios.delete('/UserProfile', {
                    params: {
                        platformID: pn.platformID,
                        NumberID: pn.numberID,
                        platformuserid: pn.userID,
                    },
                });
            }
            successToast({
                title: `Deallocated ${numbersWithUsers.length} user(s) successfully`,
            });
            await refetchPlatformNumbers();
        } catch (error) {
            errorToast({
                title: 'Failed to deallocate users',
                description: error.message,
            });
        } finally {
            setIsBusy(false);
        }
    }, [
        rowData,
        selectedPlatformFilter,
        refetchPlatformNumbers,
        successToast,
        errorToast,
    ]);

    const handleDeallocateAllTrunks = useCallback(async () => {
        try {
            setIsBusy(true);
            const allPlatformNumbers = rowData.flatMap(
                (n) => n.platformNumbers,
            );
            const numbersToProcess = selectedPlatformFilter
                ? allPlatformNumbers.filter(
                      (pn) => pn.platformID === selectedPlatformFilter,
                  )
                : allPlatformNumbers;
            const numbersWithTrunks = numbersToProcess.filter(
                (pn) => pn.platformStatus === 0 && pn.trunk && !pn.user,
            );
            for (const pn of numbersWithTrunks) {
                await axios.put('/PlatformNumber', {
                    platformID: pn.platformID,
                    numberID: pn.numberID,
                    trunkID: '',
                    priority: pn.priority || 0,
                    overstampTrunkID: pn.overstampTrunkID || null,
                    overstampNumber: pn.overstampNumber || null,
                    operatorConnectOperatorID:
                        pn.operatorConnectOperatorID || null,
                    operatorConnectCallingProfileID:
                        pn.operatorConnectCallingProfileID || null,
                    operatorConnectUsageID: pn.operatorConnectUsageID || null,
                    overrideDefaultRegistration:
                        pn.overrideDefaultRegistration || false,
                    registrationID: pn.registrationID || null,
                    addressID: pn.addressID || null,
                    attribute1: pn.attribute1 || null,
                    attribute2: pn.attribute2 || null,
                    attribute3: pn.attribute3 || null,
                });
            }
            successToast({
                title: `Deallocated ${numbersWithTrunks.length} trunk(s) successfully`,
            });
            await refetchPlatformNumbers();
        } catch (error) {
            errorToast({
                title: 'Failed to deallocate trunks',
                description: error.message,
            });
        } finally {
            setIsBusy(false);
        }
    }, [
        rowData,
        selectedPlatformFilter,
        refetchPlatformNumbers,
        successToast,
        errorToast,
    ]);

    const handleDisassociateAll = useCallback(async () => {
        try {
            setIsBusy(true);
            const allPlatformNumbers = rowData.flatMap(
                (n) => n.platformNumbers,
            );
            const teamsPhoneMobileService = services?.find(
                (s) => s.name === 'Teams Phone Mobile',
            );
            if (!teamsPhoneMobileService) {
                errorToast({ title: 'Teams Phone Mobile service not found' });
                return;
            }
            const numbersToDisassociate = allPlatformNumbers.filter(
                (pn) => pn.operatorConnectUsageID === 'OCMobile',
            );
            if (numbersToDisassociate.length === 0) {
                errorToast({ title: 'No eligible numbers to disassociate' });
                return;
            }
            await deactivateNumbers({
                serviceID: teamsPhoneMobileService.id,
                phoneNumbers: numbersToDisassociate.map(
                    (pn) => pn.telephoneNumber,
                ),
            });
            successToast({
                title: `Disassociated ${numbersToDisassociate.length} number(s) successfully`,
            });
            await refetchPlatformNumbers();
        } catch (error) {
            errorToast({
                title: 'Failed to disassociate numbers',
                description: error.message,
            });
        } finally {
            setIsBusy(false);
        }
    }, [
        rowData,
        services,
        deactivateNumbers,
        refetchPlatformNumbers,
        successToast,
        errorToast,
    ]);

    return (
        <>
            <AGGridMain
                stateIdentifier="numberBlocksAllocationsTable"
                gridRef={tableRef}
                tableData={{
                    columns: displayConfig.columns,
                    data: displayConfig.data,
                }}
                isLoading={loading}
                masterDetail={displayConfig.masterDetail}
                detailCellRendererParams={
                    displayConfig.detailCellRendererParams
                }
                isRowMaster={displayConfig.isRowMaster}
                rowSelection="multiple"
                groupSelectsChildren
                suppressRowClickSelection={false}
                suppressAggFuncInHeader={true}
                extraComponents={
                    <div style={{ paddingLeft: '20px' }}>
                        <HStack spacing={2}>
                            <ChakraButton
                                size="sm"
                                colorScheme="red"
                                flexShrink={0}
                                onClick={onDeallocateAllUsersOpen}>
                                Deallocate All Users
                            </ChakraButton>
                            <ChakraButton
                                size="sm"
                                colorScheme="orange"
                                flexShrink={0}
                                onClick={onDeallocateAllTrunksOpen}>
                                Deallocate All Trunks
                            </ChakraButton>
                            {hasOCMobileNumbers && (
                                <ChakraButton
                                    size="sm"
                                    colorScheme="purple"
                                    flexShrink={0}
                                    onClick={onDisassociateAllOpen}>
                                    Disassociate All
                                </ChakraButton>
                            )}
                            <PlatformFilterStack
                                platformList={platformList}
                                isSelected={(platform) =>
                                    selectedPlatformFilter === platform.id
                                }
                                onSelect={(platform) =>
                                    setSelectedPlatformFilter(
                                        selectedPlatformFilter === platform.id
                                            ? undefined
                                            : platform.id,
                                    )
                                }
                            />
                        </HStack>
                    </div>
                }
                refetch={refetchPlatformNumbers}
            />

            {/* Deallocate User Modal */}
            <Modal
                isOpen={isDeallocateUserOpen}
                onClose={onDeallocateUserClose}
                isCentered>
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent>
                    <ModalHeader>Deallocate User</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {modalError ? (
                            <Alert status="error">
                                <AlertIcon />
                                <AlertDescription>
                                    {modalError}
                                </AlertDescription>
                            </Alert>
                        ) : (
                            <Alert status="warning">
                                <AlertIcon />
                                <AlertDescription>
                                    Are you sure you want to deallocate the user
                                    from this platform number?
                                </AlertDescription>
                            </Alert>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setModalError(null);
                                onDeallocateUserClose();
                            }}
                            mr={3}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="red"
                            isLoading={isBusy}
                            onClick={async () => {
                                const success =
                                    await handleDeallocateUser(selectedAction);
                                if (success) {
                                    setModalError(null);
                                    onDeallocateUserClose();
                                }
                            }}>
                            Deallocate
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Deallocate Trunk Modal */}
            <Modal
                isOpen={isDeallocateTrunkOpen}
                onClose={onDeallocateTrunkClose}
                isCentered>
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent>
                    <ModalHeader>Deallocate Trunk</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {modalError ? (
                            <Alert status="error">
                                <AlertIcon />
                                <AlertDescription>
                                    {modalError}
                                </AlertDescription>
                            </Alert>
                        ) : (
                            <Alert status="warning">
                                <AlertIcon />
                                <AlertDescription>
                                    Are you sure you want to deallocate the
                                    trunk from this platform number?
                                </AlertDescription>
                            </Alert>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setModalError(null);
                                onDeallocateTrunkClose();
                            }}
                            mr={3}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="orange"
                            isLoading={isBusy}
                            onClick={async () => {
                                const success =
                                    await handleDeallocateTrunk(selectedAction);
                                if (success) {
                                    setModalError(null);
                                    onDeallocateTrunkClose();
                                }
                            }}>
                            Deallocate
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Deallocate All Users Modal */}
            <Modal
                isOpen={isDeallocateAllUsersOpen}
                onClose={onDeallocateAllUsersClose}
                isCentered>
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent>
                    <ModalHeader>Deallocate All Users</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Alert status="warning">
                            <AlertIcon />
                            <AlertDescription>
                                Are you sure you want to deallocate all users
                                {selectedPlatformFilter
                                    ? ' for the selected platform'
                                    : ''}
                                ? This action will affect multiple platform
                                numbers.
                            </AlertDescription>
                        </Alert>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="outline"
                            onClick={onDeallocateAllUsersClose}
                            mr={3}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="red"
                            isLoading={isBusy}
                            onClick={async () => {
                                await handleDeallocateAllUsers();
                                onDeallocateAllUsersClose();
                            }}>
                            Deallocate All
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Deallocate All Trunks Modal */}
            <Modal
                isOpen={isDeallocateAllTrunksOpen}
                onClose={onDeallocateAllTrunksClose}
                isCentered>
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent>
                    <ModalHeader>Deallocate All Trunks</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Alert status="warning">
                            <AlertIcon />
                            <AlertDescription>
                                Are you sure you want to deallocate all trunks
                                {selectedPlatformFilter
                                    ? ' for the selected platform'
                                    : ''}
                                ? This action will affect multiple platform
                                numbers.
                            </AlertDescription>
                        </Alert>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="outline"
                            onClick={onDeallocateAllTrunksClose}
                            mr={3}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="orange"
                            isLoading={isBusy}
                            onClick={async () => {
                                await handleDeallocateAllTrunks();
                                onDeallocateAllTrunksClose();
                            }}>
                            Deallocate All
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Disassociate Modal */}
            <Modal
                isOpen={isDisassociateOpen}
                onClose={onDisassociateClose}
                isCentered>
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent>
                    <ModalHeader>Disassociate Number</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Alert status="warning">
                            <AlertIcon />
                            <AlertDescription>
                                Are you sure you want to disassociate this
                                operator number from your company? This action
                                cannot be undone.
                            </AlertDescription>
                        </Alert>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="outline"
                            onClick={onDisassociateClose}
                            mr={3}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="red"
                            isLoading={isBusy}
                            onClick={async () => {
                                await handleDisassociate(selectedAction);
                                onDisassociateClose();
                            }}>
                            Disassociate
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Disassociate All Modal */}
            <Modal
                isOpen={isDisassociateAllOpen}
                onClose={onDisassociateAllClose}
                isCentered>
                <ModalOverlay backdropFilter="blur(10px)" />
                <ModalContent>
                    <ModalHeader>Disassociate All Numbers</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Alert status="warning">
                            <AlertIcon />
                            <AlertDescription>
                                Are you sure you want to disassociate all
                                eligible TPM numbers from your company? This
                                will only affect numbers with no user and no
                                trunk assigned. This action cannot be undone.
                            </AlertDescription>
                        </Alert>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="outline"
                            onClick={onDisassociateAllClose}
                            mr={3}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="red"
                            isLoading={isBusy}
                            onClick={async () => {
                                await handleDisassociateAll();
                                onDisassociateAllClose();
                            }}>
                            Disassociate All
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default DeleteNumberBlocksTable;
